import { memo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getVehicleAlertsSummaryAPI } from "services/api/dashboard/api";
import {
  VehicleAlertsSummary,
  VehicleAlertType,
} from "services/api/dashboard/types";
import { Loader } from "ui/components/Loader";
import { NoResults } from "ui/components/NoResults";
import { Routes as FleetRegistrationRoutes } from "ui/modules/fleet/registrations/navigation";
import { Routes as FleetCascoRoutes } from "ui/modules/fleet/casco/navigation";
import { Routes as FleetFireExtinguisherRoutes } from "ui/modules/fleet/fireExtinguishers/navigation";
import CardHeader from "../../../common/CardHeader";
import BarTooltip, { BarTooltipRow, BarTooltipTitle } from "../../../common/HoverPopup";
import { text } from "../../../common/dashboardPalette";

// Redoslijed tipova na x-osi.
const TYPE_ORDER: VehicleAlertType[] = [
  "registration",
  "insurance",
  "casco",
  "fireextinguisher",
];

// Osiguranje nema zasebnu pretragu -> route: null (nije klikabilno, pokriveno Pretraga tabom).
const TYPE_META: Record<
  VehicleAlertType,
  { label: string; short: string; route: string | null }
> = {
  registration: {
    label: "Registracija",
    short: "Registracija",
    route: FleetRegistrationRoutes.RegistrationExpirySearchPageRouter,
  },
  insurance: {
    label: "Osiguranje",
    short: "Osiguranje",
    route: null,
  },
  casco: {
    label: "Kasko",
    short: "Kasko",
    route: FleetCascoRoutes.CascoExpirySearchPageRouter,
  },
  fireextinguisher: {
    label: "Vatrogasni aparat",
    short: "Vatrogasni",
    route: FleetFireExtinguisherRoutes.FireExtinguisherSearchPageRouter,
  },
};

// Prvi stupac = žuti gradient (istaknut), ostali sivi. Iste boje kao "istek" kartice.
const currentBar =
  "bg-gradient-to-b from-yellow-300 to-yellow-100 border-t-2 border-yellow-400 group-hover/bar:from-yellow-200 group-hover/bar:to-yellow-50";
const otherBar = "bg-slate-200 group-hover/bar:bg-yellow-200";

const AlertsStatsTab: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<VehicleAlertsSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getVehicleAlertsSummaryAPI()
      .then(setSummary)
      .catch(() => setSummary(null))
      .finally(() => setIsLoading(false));
  }, []);

  // Klik na tip -> pretraga isteka tog tipa (osiguranje nema stranicu -> nije klikabilno).
  const handleTypeClick = (type: VehicleAlertType) => {
    const route = TYPE_META[type].route;
    if (route) navigate(route);
  };

  if (isLoading) {
    return (
      <div className="relative h-full min-h-[200px]">
        <Loader isLoading={true}> </Loader>
      </div>
    );
  }

  if (!summary || summary.total === 0) {
    return <NoResults title="Nema upozorenja" />;
  }

  const rows = TYPE_ORDER.map((type) => {
    const stat = summary.byType.find((b) => b.type === type);
    return {
      type,
      ...TYPE_META[type],
      expired: stat?.expired ?? 0,
      expiring: stat?.expiring ?? 0,
      total: stat?.total ?? 0,
    };
  }).filter((r) => r.total > 0);

  const maxTotal = Math.max(...rows.map((r) => r.total), 1);

  return (
    <div className="rounded-sm px-4 py-5 bg-white">
      <CardHeader
        title="Upozorenja"
        subtitle="Istekli i pred istek — po tipu"
        className="mb-8"
        right={
          <div className="flex flex-col items-end">
            <span className={`text-2xl font-bold ${text.primary}`}>{summary.total}</span>
            <span className="text-sm">
              <span className="text-rose-600 font-medium">{summary.expired} isteklo</span>
              <span className={text.subtle}> · </span>
              <span className="text-amber-600 font-medium">{summary.expiring} pred istek</span>
            </span>
          </div>
        }
      />

      <div className="mx-auto mb-2 w-full flex flex-col">
        <div className="flex justify-center gap-4 mb-2 h-[clamp(160px,26vh,280px)]">
          {rows.map((row, i) => {
            const heightPercent = (row.total / maxTotal) * 100;
            const clickable = row.route !== null;
            const barClass = i === 0 ? currentBar : otherBar;
            return (
              <div
                key={row.type}
                className={`flex flex-col items-center w-[86px] h-full group/bar relative ${clickable ? "cursor-pointer" : ""}`}
                onClick={clickable ? () => handleTypeClick(row.type) : undefined}
              >
                <span className={`text-sm ${text.muted} mb-1`}>{row.total}</span>
                <div className="w-full flex-1 flex items-end justify-center">
                  <div
                    className={`w-full rounded-sm origin-bottom animate-growUp transition-all ${barClass}`}
                    style={{ height: `${Math.max(heightPercent, 5)}%` }}
                  />
                </div>
                <span className="text-xs text-slate-400 mt-1 w-full text-center truncate">
                  {row.short}
                </span>
                <BarTooltip>
                  <BarTooltipTitle>{row.label}</BarTooltipTitle>
                  <BarTooltipRow label="Isteklo" value={row.expired} emphasis="negative" />
                  <BarTooltipRow label="Pred istek" value={row.expiring} />
                  <BarTooltipRow label="Ukupno" value={row.total} />
                </BarTooltip>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default memo(AlertsStatsTab);
